import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  Recycle,
  Layers,
  Trash2,
  Hourglass,
  Factory,
  Leaf,
  MapPin,
  CheckCircle2,
  Sprout,
  Cloud,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import { CountUp } from "@/components/ImpactCounter";
import { api } from "@/lib/api";
import Seo, { organizationSchema, faqSchema } from "@/seo/Seo";
import { AdminSignInLink } from "@/components/AdminSignInLink";

const LANDFILL_IMG =
  "https://images.unsplash.com/photo-1642204705127-accc0dcc5779?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYW5kZmlsbCUyMHdhc3RlJTIwZW52aXJvbm1lbnR8ZW58MHx8fHwxNzg5MjU0OTY2fDA&ixlib=rb-4.1.0&q=85";
const CARPET_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/pk3i8fa7_file_00000000b77081f4affe663f0bdb30a9.png";

const FACTS = [
  {
    icon: Layers,
    stat: "Fused layers",
    title: "Built to never come apart",
    body: "Carpet is a composite — face fibres (nylon, polypropylene or polyester) fused to a latex or bitumen backing. Because the layers are bonded, they're difficult and costly to separate for clean recycling.",
  },
  {
    icon: Recycle,
    stat: "< 5%",
    title: "Recycled at all",
    body: "Industry estimates suggest less than 5% of carpet waste is successfully recycled — and only around 1% of that makes it back into new carpet in a true closed loop.",
  },
  {
    icon: Hourglass,
    stat: "1,000 yrs",
    title: "To break down",
    body: "Roughly 75% of modern carpet is synthetic, so a remnant sent to landfill can take up to a thousand years to fully decompose.",
  },
  {
    icon: Factory,
    stat: "88,700 t",
    title: "Buried in the UK each year",
    body: "Industry estimates suggest around 88,700 tonnes of flooring is sent to UK landfill annually — much of it perfectly usable roll ends, offcuts and surplus packs.",
  },
];

const SUSTAINABILITY_FAQS = [
  {
    q: "Why is carpet so hard to recycle?",
    a: "Carpet is a composite material — the face fibres are fused to a latex or bitumen backing. Separating those bonded layers cleanly is difficult and expensive, which is why industry estimates put carpet recycling rates at under 5%, with only around 1% recycled back into new carpet.",
  },
  {
    q: "How long does carpet take to decompose in landfill?",
    a: "Because roughly 75% of modern carpet is synthetic, a remnant sent to landfill can take up to 1,000 years to fully break down.",
  },
  {
    q: "How much flooring goes to landfill in the UK?",
    a: "Industry estimates suggest around 88,700 tonnes of flooring is buried in UK landfills every year — a large share of it usable surplus, roll ends and offcuts.",
  },
  {
    q: "How does Floor-Match reduce flooring waste?",
    a: "Floor-Match connects buyers to surplus and remnant flooring that independent retailers already hold in stock. Every reservation puts existing flooring to use in a real room instead of sending it to landfill — no new manufacturing, no new waste.",
  },
];

export default function Sustainability() {
  const structured = [organizationSchema(), faqSchema(SUSTAINABILITY_FAQS)];
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/impact/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <>
      <Seo
        title="Flooring waste & recycling — why surplus flooring keeps carpet out of landfill"
        description="Less than 5% of carpet is recycled and around 88,700 tonnes of flooring is buried in UK landfills each year. See why reserving surplus and remnant flooring on Floor-Match is the greener choice."
        path="/sustainability"
        keywords="carpet recycling UK, flooring landfill waste, sustainable flooring Manchester, recycle carpet remnants, surplus flooring environment, eco friendly flooring"
        structured_data={structured}
      />
      <PublicHeader />
      <main className="min-h-screen fm-grain pb-24 sm:pb-0" data-testid="sustainability-page">
        {/* Hero — dark green, matches brand */}
        <section className="relative overflow-hidden bg-[#0F3D2E]" data-testid="sustainability-hero">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(circle at 12% 18%, rgba(42,90,59,0.7), transparent 45%), radial-gradient(circle at 88% 82%, rgba(224,89,21,0.22), transparent 42%)",
            }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
            aria-hidden="true"
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-14 sm:pb-20">
            <div className="max-w-3xl space-y-6">
              <span
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#9fe0b6] bg-[#2A5A3B]/40 border border-[#2A5A3B]/60 px-3 py-1.5 rounded-full"
                data-testid="sustainability-eyebrow"
              >
                <Leaf className="w-3.5 h-3.5" aria-hidden="true" /> The green case for surplus
              </span>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
                Great flooring shouldn't
                <br className="hidden sm:block" /> cost the earth.
              </h1>
              <p className="text-lg sm:text-xl text-white/85 max-w-2xl">
                Most surplus carpet and flooring never gets a second life — it's buried in
                landfill where it can sit for centuries. Floor-Match exists to change that,
                one reserved remnant at a time.
              </p>
              <div className="flex flex-wrap gap-3 pt-1">
                <StatPill big="88,700 t" small="flooring buried in the UK each year" />
                <StatPill big="< 5%" small="of carpet is ever recycled" />
                <StatPill big="1,000 yrs" small="to break down in landfill" />
              </div>
            </div>
          </div>
        </section>

        {/* The problem — fact cards + image */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="sustainability-problem">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                The problem
              </p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
                Why carpet remnants end up in landfill
              </h2>
              <p className="text-slate-600 text-sm mt-3 max-w-xl">
                Flooring is one of the hardest household materials to recycle. The way carpet is
                made all but guarantees that offcuts and surplus rolls end up buried rather than
                reused.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {FACTS.map((f) => (
                  <div
                    key={f.title}
                    className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                    data-testid={`sustainability-fact-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#2A5A3B]/10 text-[#2A5A3B]">
                      <f.icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <div className="font-display font-bold text-2xl text-slate-900 mt-4">
                      {f.stat}
                    </div>
                    <h3 className="font-display font-semibold text-base text-slate-900 mt-1">
                      {f.title}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1.5">{f.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <img
                  src={LANDFILL_IMG}
                  alt="A large landfill site with waste piled to the horizon"
                  loading="lazy"
                  decoding="async"
                  className="rounded-2xl w-full aspect-[4/5] object-cover ring-1 ring-slate-200"
                />
                <div className="absolute -bottom-4 -left-4 bg-white border border-slate-200 rounded-xl p-4 shadow-md max-w-[15rem]">
                  <div className="flex items-center gap-2 text-[#2A5A3B]">
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      The waste problem
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 mt-2 leading-snug">
                    Perfectly usable flooring is thrown away every day simply because it was
                    surplus to one job.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* UK stat band */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24" data-testid="sustainability-stat-band">
          <div className="rounded-3xl bg-[#2A5A3B] text-white overflow-hidden">
            <div className="px-6 sm:px-12 py-12 sm:py-16 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="font-display font-bold text-6xl sm:text-7xl leading-none">
                  88,700
                  <span className="text-2xl sm:text-3xl align-top ml-2 text-[#9fe0b6]">tonnes</span>
                </div>
                <p className="text-white/85 mt-4 max-w-md">
                  of flooring is buried in UK landfills every single year — the weight of roughly
                  7,000 double-decker buses, most of it usable roll ends, offcuts and surplus packs.
                </p>
              </div>
              <div className="md:justify-self-end">
                <div className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-[#9fe0b6]">
                    <Recycle className="w-5 h-5" aria-hidden="true" />
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      Every reservation counts
                    </span>
                  </div>
                  <p className="text-white/90 text-sm mt-3">
                    There's no need to manufacture new flooring — and create new waste — when
                    quality surplus is already sitting in a warehouse near you.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Live impact — real totals from Floor-Match sales */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24" data-testid="sustainability-live-impact">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
              Our impact so far
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
              Every sale is flooring saved from landfill
            </h2>
            <p className="text-slate-600 text-sm mt-3">
              These figures update as Floor-Match retailers sell surplus stock. Weight and CO₂e are
              estimated from typical flooring densities.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            <ImpactStat
              icon={Sprout}
              value={stats?.rooms_rescued ?? 0}
              label="rooms rescued"
              sub="surplus listings sold and reused"
              testId="impact-rooms"
            />
            <ImpactStat
              icon={Recycle}
              value={stats?.weight_kg ?? 0}
              suffix=" kg"
              label="diverted from landfill"
              sub="estimated weight of flooring reused"
              testId="impact-weight"
            />
            <ImpactStat
              icon={Cloud}
              value={stats?.co2e_kg ?? 0}
              suffix=" kg"
              label="CO₂e avoided"
              sub="estimated emissions from new production"
              testId="impact-co2"
            />
          </div>
        </section>

        {/* How Floor-Match helps */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24" data-testid="sustainability-solution">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <img
                src={CARPET_IMG}
                alt="A warehouse stack of surplus carpet rolls in a range of colours and textures"
                loading="lazy"
                decoding="async"
                className="rounded-2xl w-full aspect-[5/4] object-cover ring-1 ring-slate-200"
              />
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                The Floor-Match difference
              </p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
                Reuse beats recycling.
              </h2>
              <p className="text-slate-600 mt-3 max-w-lg">
                Recycling carpet is hard. Reusing it isn't. Floor-Match matches you to surplus
                flooring that already exists — so it ends up in your room instead of the ground.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  {
                    t: "Existing stock, no new manufacturing",
                    d: "Every listing is flooring that's already been made. Choosing surplus means no new production and no new emissions.",
                  },
                  {
                    t: "Matched to your exact room",
                    d: "We only show stock big enough for your space, so you buy what you need and waste less at the offcut stage too.",
                  },
                  {
                    t: "Collected locally",
                    d: "Reserve online and collect from an independent shop near you — short local trips, not long-haul delivery.",
                  },
                ].map((item) => (
                  <li key={item.t} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#2A5A3B] shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{item.t}</div>
                      <div className="text-sm text-slate-600">{item.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/search"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-base shadow-lg shadow-[#E05915]/30 transition-colors"
                  data-testid="sustainability-cta-search"
                >
                  Find flooring that fits <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/sell"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border border-slate-200 text-slate-800 hover:border-[#2A5A3B] hover:text-[#2A5A3B] font-semibold text-base transition-colors"
                  data-testid="sustainability-cta-sell"
                >
                  <MapPin className="w-4 h-4" aria-hidden="true" /> List your surplus stock
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Sources note */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14">
          <p className="text-[11px] text-slate-400 max-w-2xl">
            Figures on this page are drawn from published flooring and carpet industry estimates on
            recycling rates, synthetic content and landfill volumes, and are provided for general
            information.
          </p>
        </section>

        <footer className="border-t border-slate-200 py-8 mt-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700 tracking-wide uppercase text-[11px]">
                FloorMatch
              </span>
              <span className="text-slate-400">·</span>
              <span className="text-[#2A5A3B] font-semibold">The perfect fit. Near you.</span>
            </div>
            <div className="flex items-center gap-4">
              <AdminSignInLink />
              <span>© 2026 FloorMatch — Made in Manchester</span>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}

function StatPill({ big, small }) {
  return (
    <span className="inline-flex flex-col bg-white/10 border border-white/20 text-white px-4 py-2.5 rounded-2xl backdrop-blur-sm">
      <span className="font-display font-bold text-xl leading-none text-[#9fe0b6]">{big}</span>
      <span className="text-xs text-white/80 mt-1">{small}</span>
    </span>
  );
}

function ImpactStat({ icon: Icon, value, suffix = "", label, sub, testId }) {
  const decimals = suffix === " kg" && value < 100 ? 1 : 0;
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-7 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid={testId}
    >
      <span className="inline-grid place-items-center w-12 h-12 rounded-xl bg-[#2A5A3B]/10 text-[#2A5A3B] mx-auto">
        <Icon className="w-6 h-6" aria-hidden="true" />
      </span>
      <div className="font-display font-bold text-4xl sm:text-5xl text-slate-900 mt-4">
        <CountUp value={value} decimals={decimals} />
        {suffix}
      </div>
      <div className="font-semibold text-slate-900 mt-2">{label}</div>
      <div className="text-sm text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
